import * as React from 'react'
import {useQuery} from '@apollo/client'
import Header from '../../components/Header/Header'
import {
  currentUserRolesQuery,
  getEmployeeDetailsFromIAM,
} from '../../queries/employeeQuery'
import RouteHandler from './RouteHandler'
import Loader from '../../components/common/Loader/Loader'
import {writeToCache} from '../../utils/graphQLUtils'
import {SearchContext, searchState, searchReducer} from '../Search/utils.search'
import {newsTargetGroupQuery} from '../../queries/newsTargetGroupQuery'
import {getUserProfileInfo} from '../../queries/userProfileQuery'
import './routeContainer.scss'
import CookieConsentWrapper from '../../components/common/CookieConsentWrapper/CookieConsentWrapper'
import {checkObjProps, isMobile} from '../../utils/genericUtils'
import {
  getDecodedLoginUserInfo,
  getLoginAsInfoFromCookie,
} from '../../utils/cookieUtils'
import MobileBottomNavWrapper from '../../components/MobileBottomNav/MobileBottomNavWrapper'
import {TargetContext} from '../../contexts/TargetContext'

const RouteContainer = () => {
  const [searchData, dispatchToSearch] = React.useReducer(
    searchReducer,
    searchState
  )

  // Fetch target groups
  const {data: targetData, loading: targetLoading} =
    useQuery(newsTargetGroupQuery)
  const tIds: any = {}
  if (targetData?.taxonomyTermQuery?.entities) {
    targetData.taxonomyTermQuery.entities
      .filter((entity: any) => entity !== null)
      .forEach((item: any) => {
        if (item) {
          tIds[item.entityLabel] = item.entityId
        }
      })
  }

  // Cookies — parse once, reuse
  const decodedUserInfo = getDecodedLoginUserInfo()
  const loginAsInfo = getLoginAsInfoFromCookie()

  // Fetch user profile info
  const emailId = loginAsInfo.email || decodedUserInfo?.emailId || ''

  const {data: profileData, loading: profileLoading} = useQuery(
    getUserProfileInfo,
    {variables: {emailIdList: [emailId]}}
  )
  const adfsUserInfo = checkObjProps(
    profileData?.getBulkUserProfileInfo,
    ['0'],
    ''
  )

  // Fetch employee details
  const empId = loginAsInfo.citrixId || decodedUserInfo?.userId || null

  const {data: employeeData, loading: employeeLoading} = useQuery(
    getEmployeeDetailsFromIAM,
    {variables: {empId}}
  )
  const employee = employeeData?.getEmployeeDetailsFromIAM

  // Fetch user roles
  const {data: rolesData, loading: rolesLoading} = useQuery(
    currentUserRolesQuery
  )
  const currentUserRoles = checkObjProps(
    rolesData?.currentUserContext,
    ['roles'],
    []
  ).map((role: any) => role.targetId)
  const isInfluencer = currentUserRoles.includes('sixt_voice')

  const userLoading = profileLoading || employeeLoading || rolesLoading

  // Build target context value
  const {profilePhotoURL = '', name = ''} = adfsUserInfo || {}
  const defaultTarget = (tIds && tIds.Franchise) || '52'
  const regionIdentifier = ''
  const citrixId = employee?.sixtId || ''
  const email = employee?.email || ''
  const contractCountry = employee?.country || ''

  const targetContextValue = React.useMemo(
    () => ({
      citrixId: loginAsInfo.citrixId || citrixId,
      email: emailId || email,
      defaultTarget,
      regionIdentifier,
      adfsUserInfo: {profilePhotoURL, name},
      additionalTargets: [],
      contractCountry,
      isInfluencer,
    }),
    [
      loginAsInfo.citrixId,
      loginAsInfo.email,
      citrixId,
      emailId,
      defaultTarget,
      profilePhotoURL,
      name,
      contractCountry,
      isInfluencer,
      regionIdentifier,
    ]
  )

  React.useEffect(() => {
    if (employee) {
      writeToCache(employee)
    }
  }, [employee])

  return (
    <div>
      {targetLoading || userLoading ? (
        <Loader />
      ) : (
        !!employee && (
          <TargetContext.Provider value={targetContextValue}>
            <div className="route-container">
              <SearchContext.Provider value={{searchData, dispatchToSearch}}>
                <Header />
                <RouteHandler employee={employee} />
                {isMobile() && <MobileBottomNavWrapper />}
                <CookieConsentWrapper citrixId={employee.sixtId} />
              </SearchContext.Provider>
            </div>
          </TargetContext.Provider>
        )
      )}
    </div>
  )
}

export default React.memo(RouteContainer)
